import type {
  IPrimitivePaneRenderer, IPrimitivePaneView, ISeriesApi, ISeriesPrimitive, SeriesAttachedParameter, SeriesType, Time,
} from "lightweight-charts";

export interface Band {
  low: number;
  high: number;
  /** Canvas colours (rgba strings): the fill between the edges and the dashed edges. */
  fill: string;
  edge: string;
}

/**
 * Shades a horizontal price band across the pane of the series it is attached
 * to, behind the series. Used for the RSI "reset" band the pullback checklist
 * grades (35–60 for buys, 40–65 for sells), so the rule is visible on the
 * chart instead of being a number in a card.
 */
export class BandPrimitive implements ISeriesPrimitive<Time> {
  private series: ISeriesApi<SeriesType, Time> | null = null;
  private requestUpdate: (() => void) | null = null;
  private readonly views: readonly IPrimitivePaneView[];

  constructor(private band: Band) {
    const renderer: IPrimitivePaneRenderer = { draw: () => {}, drawBackground: target => this.draw(target) };
    this.views = [{ zOrder: () => "bottom", renderer: () => renderer }];
  }

  attached({ series, requestUpdate }: SeriesAttachedParameter<Time>) {
    this.series = series;
    this.requestUpdate = requestUpdate;
  }

  detached() {
    this.series = null;
    this.requestUpdate = null;
  }

  paneViews() {
    return this.views;
  }

  setBand(band: Band) {
    this.band = band;
    this.requestUpdate?.();
  }

  private draw(target: Parameters<IPrimitivePaneRenderer["draw"]>[0]) {
    const s = this.series;
    if (!s) return;
    const yHigh = s.priceToCoordinate(this.band.high);
    const yLow = s.priceToCoordinate(this.band.low);
    if (yHigh === null || yLow === null) return;
    target.useMediaCoordinateSpace(({ context: ctx, mediaSize }) => {
      const top = Math.min(yHigh, yLow);
      const h = Math.abs(yLow - yHigh);
      ctx.fillStyle = this.band.fill;
      ctx.fillRect(0, top, mediaSize.width, h);
      ctx.strokeStyle = this.band.edge;
      ctx.lineWidth = 1;
      ctx.setLineDash([3, 3]);
      ctx.beginPath();
      ctx.moveTo(0, Math.round(top) + 0.5);
      ctx.lineTo(mediaSize.width, Math.round(top) + 0.5);
      ctx.moveTo(0, Math.round(top + h) + 0.5);
      ctx.lineTo(mediaSize.width, Math.round(top + h) + 0.5);
      ctx.stroke();
      ctx.setLineDash([]);
    });
  }
}
